import React, { useEffect, useState } from 'react';
import {
  Row,
  Col,
  Form,
  FloatingLabel,
  OverlayTrigger,
  Popover,
  Button
} from 'react-bootstrap';
import DatePicker from 'components/base/DatePicker';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowsDownToLine } from '@fortawesome/free-solid-svg-icons';
import CallResultsData from './CallResultsData';

interface CallResultsFilterProps {
  bxpcampaignid: number;
}

const CallResultsFilter: React.FC<CallResultsFilterProps> = ({
  bxpcampaignid
}) => {
  const [selectedDateRange, setSelectedDateRange] = useState<Date[]>([]);
  const [dateRangeOption, setDateRangeOption] = useState('last30');
  const [appliedFilters, setAppliedFilters] = useState({
    startDate: new Date(),
    endDate: new Date()
  });
  const [showPopover, setShowPopover] = useState(false);
  const [shouldBounce, setShouldBounce] = useState(false);

  const getDefaultDateRange = () => {
    const today = new Date();
    const startDate = new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000);
    setDateRangeOption('last30');
    return [startDate, today];
  };

  const formatDateRange = (dates: Date[]) => {
    if (dates.length === 2) {
      return `${dates[0].toLocaleDateString()} - ${dates[1].toLocaleDateString()}`;
    }
    return '';
  };

  useEffect(() => {
    const defaultRange = getDefaultDateRange();
    setSelectedDateRange(defaultRange);
    setAppliedFilters({
      startDate: defaultRange[0],
      endDate: defaultRange[1]
    });
  }, [bxpcampaignid]);

  const handleDateChange = (date: Date[]) => {
    if (date.length === 2) {
      setSelectedDateRange(date);
      setShowPopover(false);
      setShouldBounce(true);
    }
  };

  const applyFilters = () => {
    setAppliedFilters({
      startDate: selectedDateRange[0],
      endDate: selectedDateRange[1]
    });
    setShouldBounce(false);
  };

  const handleDateRangeOptionChange = (value: string) => {
    setDateRangeOption(value);
    let startDate: Date, endDate: Date;
    const today = new Date();

    switch (value) {
      case 'last30':
        startDate = new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000);
        endDate = today;
        break;
      case 'last3months':
        startDate = new Date(
          today.getFullYear(),
          today.getMonth() - 3,
          today.getDate()
        );
        endDate = today;
        break;
      case 'thisyear':
        startDate = new Date(today.getFullYear(), 0, 1);
        endDate = today;
        break;
      default:
        return; // Para 'custom', no cerramos el popover
    }

    setSelectedDateRange([startDate, endDate]);
    setShowPopover(false);
    setShouldBounce(true);
  };

  const datePopover = (
    <Popover id="date-range-popover" className="shadow-lg lh-sm fs-9">
      <Popover.Body>
        <p>Select a date range option:</p>
        <div className="mb-3">
          {['last30', 'last3months', 'thisyear', 'custom'].map(option => (
            <div key={option} className="form-check form-switch mb-0">
              <label className="d-flex fs-9 justify-content-start align-items-center mx-2">
                <input
                  type="checkbox"
                  value={option}
                  checked={dateRangeOption === option}
                  onChange={() => handleDateRangeOptionChange(option)}
                  className="form-check-input pt-0"
                />
                <div className="fs-9 fw-semibold pt-1 px-2">
                  {option === 'last30' && 'Last 30 days'}
                  {option === 'last3months' && 'Last 3 months'}
                  {option === 'thisyear' && 'This year'}
                  {option === 'custom' && 'Custom range'}
                </div>
              </label>
            </div>
          ))}
        </div>
        {dateRangeOption === 'custom' && (
          <>
            <p>Select a custom date range from the date picker:</p>
            <DatePicker
              value={selectedDateRange}
              onChange={handleDateChange}
              options={{
                dateFormat: 'M j, Y',
                mode: 'range',
                onClose: () => setShowPopover(false)
              }}
            />
          </>
        )}
      </Popover.Body>
    </Popover>
  );

  return (
    <>
      <Row className="mb-1 mx-0 p-0 justify-content-start align-items-center">
        <div className="text-start px-0 pt-2">
          <label className="form-label px-2 text-primary">
            Call data filters {' (' + bxpcampaignid + ')'}
          </label>
        </div>
        <div className="d-flex flex-wrap pb-2">
          <Row>
            <Col className="p-0 me-1">
              <FloatingLabel controlId="dateRange" label="Date Range">
                <OverlayTrigger
                  trigger="click"
                  placement="bottom-start"
                  overlay={datePopover}
                  rootClose={false}
                  show={showPopover}
                  onToggle={nextShow => setShowPopover(nextShow)}
                >
                  <Form.Control
                    type="text"
                    value={formatDateRange(selectedDateRange)}
                    readOnly
                    style={{ width: '185px' }}
                  />
                </OverlayTrigger>
              </FloatingLabel>
            </Col>
            <Col className="d-flex justify-content-start align-items-center">
              <Button
                variant="link"
                onClick={applyFilters}
                className="px-1 pt-4"
              >
                <FontAwesomeIcon
                  icon={faArrowsDownToLine}
                  size="xl"
                  bounce={shouldBounce}
                />
              </Button>
            </Col>
          </Row>
        </div>
      </Row>
      <div className="mx-n4 px-4 mx-lg-n6 px-lg-6 bg-body-emphasis pt-0 mt-0 mb-0 border-top">
        <Row>
          <CallResultsData
            bxpcampaignid={bxpcampaignid}
            startDate={appliedFilters.startDate}
            endDate={appliedFilters.endDate}
          />
        </Row>
      </div>
    </>
  );
};

export default CallResultsFilter;
